import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  TablePagination,
  Chip,
  TableSortLabel,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import { ProtokollService } from '../services/protokollService';
import XmlViewerDialog from '../components/XmlViewerDialog';
import { ProtokollEintrag, ProtokollFilter } from '../types/protokoll';

type SortDirection = 'asc' | 'desc';

const ProtokollPage: React.FC = () => {
  const [data, setData] = useState<ProtokollEintrag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [sortBy, setSortBy] = useState<string>('gesendetAm');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  const today = new Date().toISOString().split('T')[0];
  const oneMonthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [filter, setFilter] = useState<ProtokollFilter>({
    von: oneMonthAgo,
    bis: today,
    kbaAntragsId: '',
    fin: '',
    kennzeichenBezirk: ''
  });

  const [xmlDialog, setXmlDialog] = useState<{ open: boolean; title: string; content: string }>({
    open: false,
    title: '',
    content: ''
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ProtokollService.getProtokoll(filter, page, rowsPerPage, sortBy, sortDir);
      setData(response.content);
      setTotalElements(response.totalElements);
    } catch (err: any) {
      console.error('Fehler beim Laden der Protokolldaten:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Protokolldaten');
    } finally {
      setLoading(false);
    }
  }, [filter, page, rowsPerPage, sortBy, sortDir]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = () => {
    setPage(0);
    loadData();
  };

  const handleClearFilter = () => {
    setFilter({
      von: oneMonthAgo,
      bis: today,
      kbaAntragsId: '',
      fin: '',
      kennzeichenBezirk: ''
    });
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
  };

  const handleExportCsv = () => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    ProtokollService.exportToCsv(data, `protokoll_${timestamp}.csv`);
  };

  const handleShowXml = (title: string, content: string | null) => {
    setXmlDialog({
      open: true,
      title,
      content: content || 'Keine Daten vorhanden'
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'ABGESCHLOSSEN':
        return 'success';
      case 'GESENDET':
      case 'IN_BEARBEITUNG':
        return 'info';
      case 'FEHLER':
      case 'ABGELEHNT':
        return 'error';
      case 'ERSTELLT':
        return 'warning';
      default:
        return 'default';
    }
  };

  const columns = [
    { id: 'antragId', label: 'ID', sortable: true },
    { id: 'kbaAntragsId', label: 'KBA-Antrags-ID', sortable: true },
    { id: 'gesendetAm', label: 'Gesendet', sortable: true },
    { id: 'username', label: 'Benutzer', sortable: true },
    { id: 'fin', label: 'FIN', sortable: true },
    { id: 'kennzeichen', label: 'Kennz.', sortable: true },
    { id: 'geschaeftsvorfallTyp', label: 'Typ', sortable: true },
    { id: 'status', label: 'Status', sortable: true },
    { id: 'xml', label: 'XML', sortable: false }
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Protokollierung</Typography>
        <Box>
          <Button startIcon={<RefreshIcon />} onClick={loadData} sx={{ mr: 1 }}>
            Aktualisieren
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportCsv}
            disabled={data.length === 0}
          >
            CSV Export
          </Button>
        </Box>
      </Box>

      {/* Filter */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filter
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            label="Von"
            type="date"
            size="small"
            value={filter.von || ''}
            onChange={(e) => setFilter({ ...filter, von: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="Bis"
            type="date"
            size="small"
            value={filter.bis || ''}
            onChange={(e) => setFilter({ ...filter, bis: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="KBA-Antrags-ID"
            size="small"
            value={filter.kbaAntragsId || ''}
            onChange={(e) => setFilter({ ...filter, kbaAntragsId: e.target.value })}
            sx={{ minWidth: 180 }}
          />
          <TextField
            label="FIN"
            size="small"
            value={filter.fin || ''}
            onChange={(e) => setFilter({ ...filter, fin: e.target.value })}
            sx={{ minWidth: 180 }}
          />
          <TextField
            label="Kennzeichen-Bezirk"
            size="small"
            value={filter.kennzeichenBezirk || ''}
            onChange={(e) => setFilter({ ...filter, kennzeichenBezirk: e.target.value })}
            sx={{ minWidth: 150 }}
          />
          <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>
            Suchen
          </Button>
          <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleClearFilter}>
            Zurücksetzen
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabelle */}
      <Paper elevation={3}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.id}>
                    {column.sortable ? (
                      <TableSortLabel
                        active={sortBy === column.id}
                        direction={sortBy === column.id ? sortDir : 'asc'}
                        onClick={() => handleSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    Keine Protokolleinträge gefunden
                  </TableCell>
                </TableRow>
              ) : (
                data.map((entry) => (
                  <TableRow key={`${entry.antragId}-${entry.gesendetAm}`} hover>
                    <TableCell>
                      <Chip label={entry.antragId} size="small" />
                    </TableCell>
                    <TableCell>{entry.kbaAntragsId || '-'}</TableCell>
                    <TableCell>{formatDate(entry.gesendetAm)}</TableCell>
                    <TableCell>
                      <Tooltip title={`${entry.nutzerVorname} ${entry.nutzerNachname}`}>
                        <span>{entry.username}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={entry.fin || '-'}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {entry.fin || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{entry.kennzeichen || '-'}</TableCell>
                    <TableCell>
                      <Tooltip title={entry.geschaeftsvorfallTyp}>
                        <Chip
                          label={entry.geschaeftsvorfallTyp.substring(0, 10) + (entry.geschaeftsvorfallTyp.length > 10 ? '…' : '')}
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={entry.status}
                        size="small"
                        color={getStatusColor(entry.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="Request XML anzeigen">
                          <IconButton
                            size="small"
                            onClick={() => handleShowXml('Request XML', entry.requestXml)}
                            disabled={!entry.requestXml}
                          >
                            <CodeIcon fontSize="small" color={entry.requestXml ? 'primary' : 'disabled'} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Response XML anzeigen">
                          <IconButton
                            size="small"
                            onClick={() => handleShowXml('Response XML', entry.responseXml)}
                            disabled={!entry.responseXml}
                          >
                            <CodeIcon fontSize="small" color={entry.responseXml ? 'success' : 'disabled'} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} von ${count !== -1 ? count : `mehr als ${to}`}`
          }
        />
      </Paper>

      {/* XML Dialog */}
      <XmlViewerDialog
        open={xmlDialog.open}
        title={xmlDialog.title}
        content={xmlDialog.content}
        onClose={() => setXmlDialog(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default ProtokollPage;
